/**
 * Mobile Mart - Predictive Live Search
 * Queries native Shopify /search/suggest.json API
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.isOpen = false;

    if (this.input) {
      this.input.addEventListener('input', this.debounce((e) => {
        this.onChange(e);
      }, 300).bind(this));
    }

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) && this.isOpen) {
        this.close();
      }
    });
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async onChange() {
    const query = this.input.value.trim();
    if (!query || query.length < 2) {
      this.close();
      return;
    }

    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last`);
      if (res.ok) {
        const data = await res.json();
        this.renderResults(data.resources.results, query);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  }

  renderResults(results, query) {
    if (!this.resultsContainer) return;

    const products = results.products || [];
    const collections = results.collections || [];

    if (products.length === 0 && collections.length === 0) {
      this.resultsContainer.innerHTML = `
        <div style="padding: 16px; text-align: center; color: var(--color-text-muted); font-size: 0.9rem;">
          No results found for "${query}". Try searching for iPhone, Samsung, AirPods, or MacBook.
        </div>
      `;
      this.open();
      return;
    }

    let html = '<div class="mm-search-suggestions" style="padding: 12px; background: #fff; border-radius: 12px; box-shadow: var(--shadow-lg); border: 1px solid var(--color-border); max-height: 400px; overflow-y: auto;">';

    if (collections.length > 0) {
      html += '<div style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: var(--color-accent); margin-bottom: 8px;">Categories</div>';
      html += '<div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px;">';
      collections.forEach(col => {
        html += `<a href="${col.url}" style="padding: 4px 10px; background: #f1f5f9; border-radius: 9999px; font-size: 0.8rem; font-weight: 600;">${col.title}</a>`;
      });
      html += '</div>';
    }

    if (products.length > 0) {
      html += '<div style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: var(--color-accent); margin-bottom: 8px;">Products</div>';
      products.forEach(p => {
        html += `
          <a href="${p.url}" style="display: flex; align-items: center; gap: 12px; padding: 8px; border-radius: 8px; transition: background 0.2s;" onmouseover="this.style.background='#f8fafc'" onmouseout="this.style.background='transparent'">
            <img src="${p.image || ''}" alt="${p.title}" style="width: 44px; height: 44px; object-fit: contain; border-radius: 6px; background: #f1f5f9; padding: 2px;">
            <div style="flex: 1;">
              <div style="font-size: 0.875rem; font-weight: 600; color: var(--color-text); line-height: 1.3;">${p.title}</div>
              <div style="font-size: 0.8rem; font-weight: 700; color: var(--color-primary);">${p.price ? 'AED ' + (p.price / 100).toFixed(2) : ''}</div>
            </div>
          </a>
        `;
      });
    }

    html += `
      <div style="border-top: 1px solid var(--color-border); padding-top: 8px; margin-top: 8px; text-align: center;">
        <a href="/search?q=${encodeURIComponent(query)}" style="font-size: 0.85rem; font-weight: 700; color: var(--color-accent);">View all results for "${query}" &rarr;</a>
      </div>
    </div>`;

    this.resultsContainer.innerHTML = html;
    this.open();
  }

  open() {
    this.resultsContainer.style.display = 'block';
    this.isOpen = true;
  }

  close() {
    if (this.resultsContainer) this.resultsContainer.style.display = 'none';
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
